// blogArticles is sourced from per-post modules; see content/blog/_registry.ts
// (split out of this file by scripts/ops/split-blog-data.mjs to keep posts maintainable).
import { blogArticles } from '@/content/blog/_registry'

// Blog article types and data for Tesla Lock Sound

/** Reference to a sound for embedding in blog content */
export interface SoundReference {
  /** Sound title to search for */
  title: string
  /** Category to filter search (optional but recommended for accuracy) */
  category?: string
  /** Sound ID if known (skips search) */
  id?: string
}

export interface BlogArticle {
  slug: string
  title: string
  excerpt: string
  content: string // MDX-style content
  author: string
  publishedAt: string
  updatedAt?: string
  category: BlogCategory
  tags: string[]
  readingTime: number // in minutes
  featured: boolean
  image?: string
  /** Optional sound references for embedding audio previews */
  soundReferences?: SoundReference[]
  /** Fetch sounds from this category for embedding (alternative to soundReferences) */
  soundCategory?: string
  /** How many sounds to display (default: 10) */
  soundLimit?: number
  /** Maps H3 heading text to sound IDs for inline embedding */
  inlineSounds?: Record<string, string>
  /** The body is itself a step-by-step install walkthrough, so the post skips the closing install steps. */
  walksThroughInstall?: boolean
}

export type BlogCategory =
  | 'tutorials'
  | 'news'
  | 'tips-tricks'
  | 'model-guides'
  | 'sound-spotlights'

export const categoryLabels: Record<BlogCategory, string> = {
  'tutorials': 'Tutorials',
  'news': 'Tesla news',
  'tips-tricks': 'Tips & tricks',
  'model-guides': 'Model guides',
  'sound-spotlights': 'Sound spotlights'
}

// Re-exported so existing consumers (`import { blogArticles } from '@/lib/blog-data'`)
// keep working unchanged.
export { blogArticles }

// Helper functions
export function getArticleBySlug(slug: string): BlogArticle | undefined {
  return blogArticles.find(article => article.slug === slug)
}

export function getArticlesByCategory(category: BlogCategory): BlogArticle[] {
  return blogArticles.filter(article => article.category === category)
}

export function getFeaturedArticles(): BlogArticle[] {
  return blogArticles.filter(article => article.featured)
}

export function getRecentArticles(limit: number = 5): BlogArticle[] {
  return blogArticles
    .toSorted((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
    .slice(0, limit)
}

export function getRelatedArticles(currentSlug: string, limit: number = 3): BlogArticle[] {
  const currentArticle = getArticleBySlug(currentSlug)
  if (!currentArticle) return []

  return blogArticles.filter(article =>
    article.slug !== currentSlug && (
      article.category === currentArticle.category ||
      article.tags.some(tag => currentArticle.tags.includes(tag))
    )
  ).slice(0, limit)
}

export function getAllTags(): string[] {
  const tags = new Set<string>()
  blogArticles.forEach(article => {
    article.tags.forEach(tag => tags.add(tag))
  })
  return Array.from(tags).sort()
}
